import { useNavigate } from "react-router-dom";
import {
  ChevronRight, Calendar, MessageSquare, FolderOpen,
  CreditCard, ClipboardList, Bell, ShoppingBag, Camera, UtensilsCrossed,
} from "lucide-react";
import { useNotificationCounts } from "@/hooks/useNotificationCounts";

const featureCards = [
  { key: "activityFeed",        title: "Daily Activity",   description: "Meals, naps, learning notes, and teacher moments.",        icon: Calendar,       iconBg: "bg-emerald-100", iconColor: "text-emerald-700", titleColor: "text-emerald-700", path: "/parent/activity",                      countKey: "activity"      },
  { key: "messages",            title: "Messages",         description: "Send a message to your center and see replies.",            icon: MessageSquare,  iconBg: "bg-violet-100",  iconColor: "text-violet-700",  titleColor: "text-violet-700",  path: "/parent/messages",                      countKey: "messages"      },
  { key: "documentUploads",     title: "Enrollment Documents (PDF)", description: "Download blank forms, upload signed ones, and see what expires.",            icon: FolderOpen,     iconBg: "bg-sky-100",     iconColor: "text-sky-700",     titleColor: "text-sky-700",     path: "/parent/documents",                     countKey: "documents"     },
  { key: "invoicesAndPayments", title: "Quick Pay",        description: "Send a payment to your center for fees or contributions.",  icon: CreditCard,     iconBg: "bg-amber-100",   iconColor: "text-amber-700",   titleColor: "text-amber-700",   path: "/parent/billing",                       countKey: "quickPay"      },
  { key: "enrollmentForms",     title: "Enrollment Forms (Custom Digital)", description: "Fill out and submit your center's online enrollment forms.",        icon: ClipboardList,  iconBg: "bg-teal-100",    iconColor: "text-teal-700",    titleColor: "text-teal-700",    path: "/parent/forms",                         countKey: null            },
  { key: "centerAnnouncements", title: "Announcements",    description: "Center-wide reminders, pickups, and schedule updates.",     icon: Bell,           iconBg: "bg-rose-100",    iconColor: "text-rose-700",    titleColor: "text-rose-700",    path: "/parent/activity?filter=announcement",  countKey: "announcements" },
  { key: "itemsNeeded",         title: "Items Needed",     description: "Requested supplies, claim items, and reminders.",           icon: ShoppingBag,    iconBg: "bg-cyan-100",    iconColor: "text-cyan-700",    titleColor: "text-cyan-700",    path: "/parent/activity?filter=project_needs", countKey: "itemsNeeded"   },
  { key: "artworkAndPhotos",    title: "Artwork & Photos", description: "Shared memories from the classroom.",                      icon: Camera,         iconBg: "bg-orange-100",  iconColor: "text-orange-700",  titleColor: "text-orange-700",  path: "/parent/activity?filter=class_project", countKey: "artwork"       },
  { key: "menus",               title: "Menus",            description: "Monthly meal menus uploaded by your center.",              icon: UtensilsCrossed,iconBg: "bg-yellow-100",  iconColor: "text-yellow-700",  titleColor: "text-yellow-700",  path: "/parent/activity?filter=menu",          countKey: "menus"         },
];

export const ParentPortalDashboard = ({ portalData }) => {
  const navigate = useNavigate();
  const { counts, markSeen } = useNotificationCounts();

  const handleTileClick = (feature) => {
    if (!feature.path) return;
    if (feature.countKey) markSeen(feature.countKey);
    navigate(feature.path);
  };

  return (
    <div className="space-y-8" data-testid="parent-portal-dashboard">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden rounded-[2.2rem] shadow-2xl"
        style={{ background: "linear-gradient(135deg, #0b4f4c 0%, #0f6b82 55%, #c96c1a 100%)" }}
        data-testid="parent-portal-hero"
      >
        {/* Hero image — clearly visible, gradient fallback if missing */}
        <img
          src={portalData.heroImageUrl}
          alt=""
          aria-hidden="true"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 h-full w-full object-cover opacity-80"
          data-testid="parent-portal-hero-image"
        />
        {/* Bottom gradient overlay — keeps text readable without killing the photo */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />

        <div className="relative grid gap-6 px-6 py-8 text-white lg:grid-cols-[1.15fr_0.85fr] lg:px-10 lg:py-10">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <img
                src="/simple-sign-in-logo.png"
                alt="Center logo"
                className="h-14 w-14 rounded-2xl bg-white/95 p-2 shadow-lg"
                data-testid="parent-portal-logo"
              />
              <span
                className="rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm"
                data-testid="parent-portal-trial-badge"
              >
                {portalData.trialLabel}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/70" data-testid="parent-portal-eyebrow">
                Parent Portal
              </p>
              <h1 className="text-4xl font-bold leading-tight sm:text-5xl" data-testid="parent-portal-title">
                Welcome, {portalData.parentName}
              </h1>
              <p className="mt-3 max-w-2xl text-sm text-white/80 sm:text-base" data-testid="parent-portal-description">
                View updates, documents, messages, and billing for your children.
              </p>
            </div>
          </div>

          <div
            className="self-center rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md"
            data-testid="parent-portal-children-summary"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Your children</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {portalData.children.map((child) => (
                <span
                  key={child.id}
                  className="rounded-full bg-white/90 px-3 py-1.5 text-sm font-semibold text-slate-800 shadow-sm"
                  data-testid={`parent-portal-child-${child.id}`}
                >
                  {child.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature cards ────────────────────────────────────────────── */}
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {featureCards
          .filter((feature) => portalData.visibility?.[feature.key] !== false)
          .map((feature) => {
            const badgeCount = feature.countKey ? counts[feature.countKey] : 0;
            const Icon = feature.icon;
            return (
              <div
                key={feature.key}
                onClick={() => handleTileClick(feature)}
                className="group relative cursor-pointer rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                data-testid={`parent-portal-feature-${feature.key}`}
              >
                {badgeCount > 0 && (
                  <span
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow-md"
                    data-testid={`parent-portal-badge-${feature.key}`}
                  >
                    {badgeCount > 9 ? "9+" : badgeCount}
                  </span>
                )}

                <div className="flex items-start justify-between gap-2">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${feature.iconBg}`}>
                    <Icon className={`h-5 w-5 ${feature.iconColor}`} />
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500 mt-3" />
                </div>

                <div className="mt-3">
                  <p className={`text-sm font-semibold ${feature.titleColor}`}>{feature.title}</p>
                  <p className="mt-1 text-sm leading-snug text-slate-500">{feature.description}</p>
                </div>
              </div>
            );
          })}
      </section>
    </div>
  );
};
